import {
  ArrowLeftRight,
  ArrowUpRight,
  Coins,
  Download,
  Eye,
  Landmark,
  MoreVertical,
  RefreshCcw,
  ScanLine,
  Snowflake,
  Sun,
  Trash2,
} from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Flag from "react-world-flags";

import { canPayBrebQr } from "@/app/auth/canPayBrebQr";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { WalletSendPanel } from "@/modules/dashboard/dispersions/components/WalletSend/WalletSendPanel";
import { WalletSwapPanel } from "@/modules/dashboard/swaps/components/WalletSwap/WalletSwapPanel";
import { canConvertFrom } from "@/modules/dashboard/swaps/utils/walletSwap";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaDropdownMenu, type ColocaDropdownItem } from "@/shared/components/overlays/ColocaDropdownMenu";
import { BrebKeyEntryCard } from "@/shared/components/payments/breb-key/BrebKeyEntryCard";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import { useBalancesHidden } from "@/shared/hooks/useBalancesHidden";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { isPositiveDecimal } from "@/shared/utils/decimal";
import { formatRail } from "@/shared/utils/railDisplay";

import { getColorForCurrency } from "../constants/walletColors.constants";
import { getMethodsForCurrency, isCryptoCurrency } from "../constants/walletMethods.constants";
import {
  CURRENCY_TO_COUNTRY,
  CURRENCY_TO_DISPERSION_CODE,
  CURRENCY_TO_FLAG_CODE,
  PAY_QR_PARAM,
  STATEMENT_MOVEMENT_PARAM,
  STATEMENT_WALLET_PARAM,
} from "../constants/wallets.constants";
import { useOperationLabels } from "../hooks/useOperationLabels";
import { useStatementMovement } from "../hooks/useStatementMovement";
import type { RuntimeWallet, RuntimeWalletTransaction } from "../types/payments-wallets.types";
import type { RecentTransaction, Wallet } from "../types/wallets-api.types";
import { currencyFractionDigits, resolveLocale } from "../utils/formatWalletAmount";
import { isWalletDeletable } from "../utils/walletOrder";
import { ArgentinaDepositModal } from "./ArgentinaDepositModal";
import { BalanceVisibilityToggle } from "./BalanceVisibilityToggle";
import { BrazilDepositModal } from "./BrazilDepositModal";
import { type ColombiaDepositView, ColombiaDepositModal } from "./ColombiaDepositModal";
import { DeleteWalletDialog } from "./DeleteWalletDialog";
import { FreezeWalletModal } from "./FreezeWalletModal";
import { MexicoDepositModal } from "./MexicoDepositModal";
import { RecentActivityList } from "./RecentActivityList";
import { UnfreezeWalletDialog } from "./UnfreezeWalletDialog";
import { WalletStatementDetailDialog } from "./WalletStatementDetailDialog";

type ActiveWalletCardProps = {
  wallet: RuntimeWallet;
  recent: RecentTransaction[];
  isLoadingRecent: boolean;
  /**
   * Refreshes THIS wallet only. Takes the id so the handler can stay referentially stable across the grid
   * (the card is memoized); a per-card closure built in the parent would defeat that on every render.
   */
  onRefresh?: (walletId: string) => Promise<void>;
  /** Hides the Cargar/Enviar actions and deposit modals — used by the INTERNAL per-org reader (view only). */
  readOnly?: boolean;
  /**
   * Every wallet of the organization, for the send panel's origin and the convert panel's two ends; without
   * it the panel sends from this one and there is nothing to convert into.
   */
  sendOrigins?: RuntimeWallet[];
  /** Only for staff on the top-up allowlist: adds "Top up" to the staff menu of an active wallet. */
  onTopUp?: (wallet: RuntimeWallet) => void;
  /**
   * Same allowlist: adds "Debit balance" (a manual send made outside the runtime) to the staff menu of an
   * active wallet with money available.
   */
  onDebit?: (wallet: RuntimeWallet) => void;
  /**
   * Offers "Delete wallet" while the wallet looks never used, and is told once the backend deleted it. Like
   * `onRefresh`, it takes the id so the parent can pass one stable handler to the whole grid.
   */
  onDeleted?: (walletId: string) => void;
};

const ActiveWalletCardComponent = ({
  wallet,
  recent,
  isLoadingRecent,
  onRefresh,
  readOnly = false,
  sendOrigins,
  onTopUp,
  onDebit,
  onDeleted,
}: ActiveWalletCardProps) => {
  const { t, activeLanguage } = useTranslations("wallets");
  const { user, isInternalUser, can } = useAuth();
  const navigate = useNavigate();
  const operationLabels = useOperationLabels();
  const { hidden: balancesHidden } = useBalancesHidden();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [depositView, setDepositView] = useState<ColombiaDepositView>("deposit");
  const [depositKeyId, setDepositKeyId] = useState<string | null>(null);
  const [isFreezeOpen, setIsFreezeOpen] = useState(false);
  const [isUnfreezeOpen, setIsUnfreezeOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);
  // Remounted per opening, so a closed panel never comes back with the last send half typed.
  const [sendSession, setSendSession] = useState(0);
  const [sendScanning, setSendScanning] = useState(false);
  // The send panel opened from "send it from Send instead" starts on the wallet the conversion was leaving.
  const [sendFromId, setSendFromId] = useState<string | null>(null);
  const [isSwapOpen, setIsSwapOpen] = useState(false);
  const [swapSession, setSwapSession] = useState(0);
  const [payQrHandled, setPayQrHandled] = useState(false);
  // A recent-activity row opens its statement detail in place, and the URL names it so a link reopens it.
  const [searchParams, setSearchParams] = useSearchParams();
  const openMovementId =
    searchParams.get(STATEMENT_WALLET_PARAM) === wallet.id
      ? searchParams.get(STATEMENT_MOVEMENT_PARAM)
      : null;
  const recentById = useMemo(
    () =>
      new Map(
        recent.flatMap((tx): [string, RuntimeWalletTransaction][] => (tx.raw ? [[tx.raw.id, tx.raw]] : [])),
      ),
    [recent],
  );
  const { transaction: openMovement } = useStatementMovement(wallet.id, openMovementId, recentById);
  const openMovementDetail = (tx: RuntimeWalletTransaction) => {
    const next = new URLSearchParams(searchParams);
    next.set(STATEMENT_WALLET_PARAM, wallet.id);
    next.set(STATEMENT_MOVEMENT_PARAM, tx.id);
    setSearchParams(next);
  };
  const closeMovementDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(STATEMENT_WALLET_PARAM);
    next.delete(STATEMENT_MOVEMENT_PARAM);
    setSearchParams(next, { replace: true });
  };

  const isCrypto = isCryptoCurrency(wallet.currency);
  const isCOP = wallet.currency === "COP";
  const isBRL = wallet.currency === "BRL";
  const isMXN = wallet.currency === "MXN";
  const isARS = wallet.currency === "ARS";
  // Which of these currencies this dashboard knows how to render a deposit flow for. Whether the corridor
  // can actually RUN is a separate question, answered by the runtime catalog below — a currency with a modal
  // but no active route would just walk the user into a `no_route`.
  const hasNativeModal = isCOP || isBRL || isMXN || isARS;
  const isActive = wallet.status === "active";
  const isFrozen = wallet.status === "frozen";
  const accentColor = getColorForCurrency(wallet.currency);
  const decimals = currencyFractionDigits(wallet.currency);
  // Rails come from the runtime when it can answer, and from the local mirror otherwise (the catalog fails
  // open, and a crypto wallet has no corridor at all — its chips are the mirror's by definition).
  const corridors = useAvailableCorridors(isInternalUser ? wallet.organization_id : undefined);
  const canDeposit = corridors.hasCurrency(wallet.currency, "PAYIN");
  // Deposits in maintenance still open their panel, which says so and how to reach support.
  const depositsInMaintenance = corridors.inMaintenance({ currency: wallet.currency, direction: "PAYIN" });
  const liveRails = corridors.railsFor(wallet.currency, "PAYIN");
  const methods =
    !isCrypto && !corridors.isUnknown && liveRails.length > 0
      ? liveRails.map(formatRail)
      : getMethodsForCurrency(wallet.currency);
  const countryName = CURRENCY_TO_COUNTRY[wallet.currency] ?? "";
  const dispersionCode = CURRENCY_TO_DISPERSION_CODE[wallet.currency];
  const flagCode = !isCrypto ? (CURRENCY_TO_FLAG_CODE[wallet.currency] ?? dispersionCode ?? null) : null;
  // "Send" (payout) is only available for currencies whose corridor runs on the payments runtime — the
  // dispersions view no longer offers the legacy PHP cross-currency flow for the other currencies.
  // Any active wallet can fund a dispersion now: the destination corridor is
  // chosen in the wizard, and the runtime converts when the currencies differ. A
  // frozen wallet is excluded rather than shown and then blocked downstream.
  const canSend = wallet.status === "active";
  const canPayQr =
    isCOP &&
    isActive &&
    can("payments:write") &&
    canPayBrebQr(user) &&
    corridors.isAvailable("BREB", "COL", "COP", "PAYOUT");

  const openSend = (scanning: boolean, fromId: string | null = null) => {
    setSendScanning(scanning);
    setSendFromId(fromId);
    setSendSession((value) => value + 1);
    setIsSendOpen(true);
  };

  const wantsPayQr = searchParams.get(PAY_QR_PARAM) === "1";
  if (wantsPayQr && !payQrHandled && canPayQr && !readOnly) {
    setPayQrHandled(true);
    openSend(true);
  }
  if (!wantsPayQr && payQrHandled) setPayQrHandled(false);

  useEffect(() => {
    if (!wantsPayQr || !payQrHandled) return;
    const next = new URLSearchParams(searchParams);
    next.delete(PAY_QR_PARAM);
    setSearchParams(next, { replace: true });
  }, [wantsPayQr, payQrHandled, searchParams, setSearchParams]);
  // Converting needs a second active wallet to land in: an organization with a single one sees no button.
  const organizationWallets = sendOrigins ?? [wallet];
  const canConvert = !readOnly && canConvertFrom(wallet, organizationWallets);
  // Three actions share the row, so they take their short names, and on a narrow card they drop their icons:
  // the row is a container, and below 20rem a button's icon is hidden. Narrower still, the row wraps.
  const actionIconClass = canConvert ? "@max-xs:[&>svg]:hidden" : undefined;
  const receiveLabel = canConvert ? t("card_action_receive_short") : t("card_action_load");
  const sendLabel = canConvert ? t("card_action_send_short") : t("card_action_send");
  const openSwap = () => {
    setSwapSession((value) => value + 1);
    setIsSwapOpen(true);
  };
  const sendWallet = organizationWallets.find((item) => item.id === sendFromId) ?? wallet;

  // Freeze/unfreeze are staff-only: the runtime gates both endpoints with `userTypes: [INTERNAL]` plus
  // `payments:write`, and on this view INTERNAL staff only ever reach a card through the per-org reader
  // (WalletsPage routes them to OrgWalletsReader — they have no org of their own). The write scope is the
  // backend's call: a denial arrives as 401/403 and surfaces as a toast, same as the statement hero.
  // `closed` is terminal, so it gets no status action — only the menu's read-only entry.
  const canManageStatus = isInternalUser && (isActive || isFrozen);
  // Only a wallet that never held money can go, and the backend re-checks what the card cannot see (a
  // collection or payout in that currency still open). Same write scope as activating it.
  const canDelete = !readOnly && !!onDeleted && can("payments:write") && isWalletDeletable(wallet);

  // Deposit modals still take the legacy Wallet shape; they only read `currency`/`country`. Bridge the
  // runtime wallet into that shape here (Phase 1) so the payin flows keep working unchanged.
  const legacyWallet: Wallet = {
    id: wallet.id,
    is_active: isActive ? 1 : 0,
    country: countryName,
    code: dispersionCode ?? "",
    currency: wallet.currency,
    balance: Number(wallet.balance_available),
    balance_limit: 0,
    created_at: wallet.created_at,
    updated_at: wallet.updated_at,
  };

  const statusLabel = isActive
    ? t("card_status_active")
    : isFrozen
      ? t("card_status_frozen")
      : t("card_status_closed");

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh(wallet.id);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Each dialog owns its own in-flight state, so the menu items only have to open them.
  const staffMenuItems: ColocaDropdownItem[] = [
    {
      label: t("card_menu_movements"),
      icon: Eye,
      onClick: () => navigate(`/app/wallets/${wallet.id}/movements`),
    },
  ];
  if (isActive && onTopUp) {
    staffMenuItems.push({
      label: t("card_menu_top_up"),
      icon: Landmark,
      onClick: () => onTopUp(wallet),
    });
  }
  if (isActive && onDebit && isPositiveDecimal(wallet.balance_available)) {
    staffMenuItems.push({
      label: t("card_menu_debit"),
      icon: ArrowUpRight,
      onClick: () => onDebit(wallet),
    });
  }
  if (isActive) {
    staffMenuItems.push({
      label: t("statement_freeze"),
      icon: Snowflake,
      onClick: () => setIsFreezeOpen(true),
    });
  } else if (isFrozen) {
    staffMenuItems.push({
      label: t("statement_unfreeze"),
      icon: Sun,
      onClick: () => setIsUnfreezeOpen(true),
    });
  }

  const deleteMenuItem: ColocaDropdownItem = {
    label: t("card_menu_delete"),
    icon: Trash2,
    variant: "destructive",
    onClick: () => setIsDeleteOpen(true),
  };
  // Members get a menu only when there is something in it; staff always have theirs.
  const menuItems: ColocaDropdownItem[] = isInternalUser
    ? [...staffMenuItems, ...(canDelete ? [deleteMenuItem] : [])]
    : canDelete
      ? [deleteMenuItem]
      : [];

  return (
    <>
      <article className="relative flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        {isRefreshing && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/80 backdrop-blur-sm">
            <RefreshCcw className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        <header className="border-b border-border p-4">
          <div className="mb-3 flex items-center gap-2.5">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border"
              style={{
                backgroundColor: `color-mix(in oklch, ${accentColor} 10%, transparent)`,
                borderColor: `color-mix(in oklch, ${accentColor} 24%, transparent)`,
              }}
              aria-hidden="true"
            >
              {isCrypto || !flagCode ? (
                <Coins size={16} style={{ color: accentColor }} />
              ) : (
                <Flag code={flagCode} className="h-4 w-6 rounded-[2px] object-cover" aria-hidden />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">
                {countryName || wallet.currency}
              </p>
              <p className="font-mono text-[11px] tracking-wide text-muted-foreground">{wallet.currency}</p>
            </div>
            <ColocaBadge
              variant={isActive ? "success" : isFrozen ? "warning" : "secondary"}
              className="text-[9.5px]"
            >
              <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
              {statusLabel}
            </ColocaBadge>
            {onRefresh && (
              <ColocaIconButton
                icon={RefreshCcw}
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                aria-label={t("refresh_aria")}
                disabled={isRefreshing}
              />
            )}
            {menuItems.length > 0 && (
              <ColocaDropdownMenu
                trigger={
                  <ColocaIconButton
                    icon={MoreVertical}
                    variant="ghost"
                    size="sm"
                    aria-label={t("card_actions_aria")}
                  />
                }
                items={menuItems}
                align="end"
              />
            )}
          </div>

          <div className="flex items-center gap-1">
            <ColocaAmountDisplay
              value={wallet.balance_available}
              animate
              masked={balancesHidden}
              currency={wallet.currency}
              minimumFractionDigits={decimals.min}
              maximumFractionDigits={decimals.max}
              locale={resolveLocale(activeLanguage)}
              size="lg"
              className="min-w-0 truncate text-foreground"
            />
            <BalanceVisibilityToggle />
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("card_available_label")}
          </p>
          {/* The frozen state is an anomaly, so it states its consequence and carries its own way out. */}
          {isFrozen && (
            <div className="mt-2.5 rounded-md border border-warning/30 bg-warning/10 px-2.5 py-2">
              <div className="flex items-start gap-2">
                <Snowflake size={13} className="mt-px shrink-0 text-warning" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11.5px] font-medium leading-tight text-warning">
                    {t("card_frozen_banner_title")}
                  </p>
                  {wallet.frozen_reason && (
                    <p
                      className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground"
                      title={wallet.frozen_reason}
                    >
                      {t("card_frozen_reason_label")}: {wallet.frozen_reason}
                    </p>
                  )}
                </div>
                {canManageStatus && (
                  <ColocaButton
                    variant="ghost"
                    size="sm"
                    icon={Sun}
                    onClick={() => setIsUnfreezeOpen(true)}
                    className="shrink-0"
                  >
                    {t("statement_unfreeze")}
                  </ColocaButton>
                )}
              </div>
            </div>
          )}

          {/* A container that wraps: on a narrow card the icons go first, then a button moves to a second line. */}
          {!readOnly && (
            <div className="mt-3 flex flex-wrap gap-1.5 @container">
              {isActive && hasNativeModal && !canDeposit && !depositsInMaintenance ? (
                // Wrapped in a span so the tooltip still fires: a disabled button swallows pointer events.
                <span className="flex-1" title={t("card_action_load_unavailable")}>
                  <ColocaButton
                    size="sm"
                    icon={Download}
                    disabled
                    className={cn("w-full justify-center", actionIconClass)}
                  >
                    {receiveLabel}
                  </ColocaButton>
                </span>
              ) : (
                <ColocaButton
                  size="sm"
                  icon={Download}
                  disabled={!isActive || !hasNativeModal}
                  onClick={() => {
                    setDepositView("deposit");
                    setIsDepositModalOpen(true);
                  }}
                  className={cn("flex-1 justify-center", actionIconClass)}
                >
                  {receiveLabel}
                </ColocaButton>
              )}
              {canSend ? (
                <ColocaButton
                  size="sm"
                  icon={ArrowUpRight}
                  disabled={!isActive}
                  onClick={() => {
                    if (hasNativeModal) {
                      openSend(false);
                      return;
                    }
                    navigate(`/app/dispersions?origin_currency=${wallet.currency}`);
                  }}
                  className={cn("flex-1 justify-center", actionIconClass)}
                >
                  {sendLabel}
                </ColocaButton>
              ) : (
                <span className="flex-1" title={t("card_action_send_unavailable")}>
                  <ColocaButton
                    size="sm"
                    icon={ArrowUpRight}
                    disabled
                    className={cn("w-full justify-center", actionIconClass)}
                  >
                    {sendLabel}
                  </ColocaButton>
                </span>
              )}
              {canConvert && (
                <ColocaButton
                  size="sm"
                  icon={ArrowLeftRight}
                  onClick={openSwap}
                  className={cn("flex-1 justify-center", actionIconClass)}
                >
                  {t("card_action_convert")}
                </ColocaButton>
              )}
              {canPayQr && (
                <ColocaIconButton
                  size="sm"
                  icon={ScanLine}
                  aria-label={t("card_action_pay_qr")}
                  title={t("card_action_pay_qr")}
                  onClick={() => openSend(true)}
                />
              )}
            </div>
          )}

          {!readOnly && isCOP && isActive && canDeposit && (
            <BrebKeyEntryCard
              className="mt-3"
              onOpen={() => {
                setDepositView("key");
                setDepositKeyId(null);
                setIsDepositModalOpen(true);
              }}
              onShowQr={(keyId) => {
                setDepositView("qr");
                setDepositKeyId(keyId);
                setIsDepositModalOpen(true);
              }}
            />
          )}
        </header>

        {methods.length > 0 && (
          <div className="border-b border-border px-3.5 py-3">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {isCrypto ? t("card_methods_crypto") : t("card_methods_label")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {methods.map((m) => (
                <span
                  key={m}
                  className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] tracking-wide text-foreground"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}

        <RecentActivityList
          transactions={recent}
          isLoading={isLoadingRecent}
          onViewAll={() => navigate(`/app/wallets/${wallet.id}/movements`)}
          onSelect={openMovementDetail}
        />
      </article>
      <WalletStatementDetailDialog
        transaction={openMovement}
        wallet={wallet}
        operationLabels={operationLabels}
        onClose={closeMovementDetail}
      />
      {!readOnly && isCOP && (
        <ColombiaDepositModal
          wallet={legacyWallet}
          open={isDepositModalOpen}
          initialView={depositView}
          initialKeyId={depositKeyId}
          onClose={() => setIsDepositModalOpen(false)}
        />
      )}
      {!readOnly && hasNativeModal && (
        <WalletSendPanel
          key={sendSession}
          wallet={sendWallet}
          origins={sendOrigins}
          open={isSendOpen}
          onClose={() => setIsSendOpen(false)}
          onSent={(walletId) => void onRefresh?.(walletId)}
          startScanning={sendScanning}
        />
      )}
      {canConvert && (
        <WalletSwapPanel
          key={`swap-${swapSession}`}
          wallet={wallet}
          wallets={organizationWallets}
          open={isSwapOpen}
          onClose={() => setIsSwapOpen(false)}
          onSwapped={(originId, destinationId) => {
            void onRefresh?.(originId);
            void onRefresh?.(destinationId);
          }}
          onOpenSend={
            hasNativeModal
              ? (walletId) => {
                  setIsSwapOpen(false);
                  openSend(false, walletId);
                }
              : undefined
          }
        />
      )}
      {!readOnly && isBRL && (
        <BrazilDepositModal
          wallet={legacyWallet}
          open={isDepositModalOpen}
          onClose={() => setIsDepositModalOpen(false)}
        />
      )}
      {!readOnly && isMXN && (
        <MexicoDepositModal
          wallet={legacyWallet}
          open={isDepositModalOpen}
          onClose={() => setIsDepositModalOpen(false)}
        />
      )}
      {!readOnly && isARS && (
        <ArgentinaDepositModal
          wallet={legacyWallet}
          open={isDepositModalOpen}
          onClose={() => setIsDepositModalOpen(false)}
        />
      )}
      {canManageStatus && (
        <>
          <FreezeWalletModal
            open={isFreezeOpen}
            onClose={() => setIsFreezeOpen(false)}
            wallet={wallet}
            onFrozen={() => void handleRefresh()}
          />
          <UnfreezeWalletDialog
            open={isUnfreezeOpen}
            onClose={() => setIsUnfreezeOpen(false)}
            wallet={wallet}
            onUnfrozen={() => void handleRefresh()}
          />
        </>
      )}
      {canDelete && onDeleted && (
        <DeleteWalletDialog
          open={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          wallet={wallet}
          onDeleted={onDeleted}
        />
      )}
    </>
  );
};

export const ActiveWalletCard = memo(ActiveWalletCardComponent);
